// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A React hook that manages the step-by-step progress animation for report loaders.
 *
 * Fetches the organization's asset count to calculate realistic timing,
 * then advances through steps at proportional intervals. Accelerates
 * to completion when actual data becomes ready.
 */
import { getAssets } from '@/asset/api/get-assets';
import type {
  ReportLoaderConfig,
  ReportTimingConfig,
} from '@/report/components/report-loader/types';
import { ONE_MINUTE_MS } from '@/utilities/time-constants';
import { type Span, startInactiveSpan } from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Return type from useReportLoaderProgress hook.
 */
interface ReportLoaderProgressState {
  currentStep: number;
  completedSteps: Set<number>;
  isComplete: boolean;
}

/** Minimum total animation time in ms (prevents flash for small datasets) */
const MIN_TOTAL_TIME_MS = 2000;

/** Maximum total animation time in ms (prevents excessively long waits) */
const MAX_TOTAL_TIME_MS = 30000;

/** Delay before firing onComplete callback for visual polish */
const COMPLETION_DELAY_MS = 200;

/** Fast step duration when data is ready */
const ACCELERATED_STEP_DURATION_MS = 100;

interface UseReportLoaderProgressOptions {
  /** Configuration for the specific report type */
  config: ReportLoaderConfig;
  /** When true, accelerates remaining steps and completes quickly */
  isDataReady?: boolean;
  /** Called when all steps are complete */
  onComplete?: () => void;
  /** Number of periods in the date range (for asset-period timing calculation) */
  periodCount?: number;
  /** Whether timing scales by asset-periods (count x periods) or just count */
  useAssetPeriods?: boolean;
}

/**
 * Calculates estimated total load time based on asset count and timing config.
 */
export function calculateReportEstimatedTimeMs(
  assetCount: number,
  periodCount: number,
  useAssetPeriods: boolean,
  timing: ReportTimingConfig
): number {
  const workUnits = useAssetPeriods ? assetCount * periodCount : assetCount;
  const networkTimeS = workUnits / timing.networkSpeed;
  const parsingTimeS = workUnits / timing.parsingSpeed;
  const totalTimeMs =
    (networkTimeS + parsingTimeS) * 1000 + (timing.fixedOverheadMs ?? 0);

  return Math.max(MIN_TOTAL_TIME_MS, Math.min(MAX_TOTAL_TIME_MS, totalTimeMs));
}

/**
 * Hook that manages report loader step progression and timing.
 *
 * @param options - Configuration and callbacks for the loader
 * @returns Current progress state including step index and completed steps
 */
export function useReportLoaderProgress(
  options: UseReportLoaderProgressOptions
): ReportLoaderProgressState {
  const {
    config,
    isDataReady,
    onComplete,
    periodCount = 1,
    useAssetPeriods = false,
  } = options;
  const stepCount = config.stepLabels.length;

  // Fetch asset count with a lightweight query (limit 1, page 1)
  const assetCountQuery = useQuery({
    queryKey: ['asset-count-for-loader'],
    queryFn: async () => {
      const response = await getAssets({ pageSize: 1, pageNumber: 1 });
      return response.totalCount;
    },
    staleTime: ONE_MINUTE_MS,
  });

  const assetCount = assetCountQuery.data ?? 0;
  const hasAssetCount = assetCountQuery.isSuccess && assetCount > 0;

  // Calculate step durations based on asset count and config
  const stepDurations = useMemo(() => {
    const totalTimeMs = hasAssetCount
      ? calculateReportEstimatedTimeMs(
          assetCount,
          periodCount,
          useAssetPeriods,
          config.timing
        )
      : MIN_TOTAL_TIME_MS;

    return config.stepWeights.map((weight) => Math.round(totalTimeMs * weight));
  }, [
    assetCount,
    hasAssetCount,
    config.timing,
    config.stepWeights,
    periodCount,
    useAssetPeriods,
  ]);

  // Current step index (-1 means not started, stepCount means complete)
  const [currentStep, setCurrentStep] = useState(-1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  // Sentry span for tracking report load performance
  const spanRef = useRef<Span | null>(null);
  const mountTime = useRef(performance.now());
  const dataReadyRecorded = useRef(false);

  // Create a span when the component mounts
  useEffect(() => {
    spanRef.current = startInactiveSpan({
      name: 'report.load',
      op: 'ui.report',
      attributes: {
        'report.type': config.reportTypeId,
      },
    });

    return () => {
      // End the span if component unmounts before completion
      if (spanRef.current) {
        spanRef.current.end();
        spanRef.current = null;
      }
    };
  }, [config.reportTypeId]);

  // Record when data becomes ready
  useEffect(() => {
    if (isDataReady && !dataReadyRecorded.current && spanRef.current) {
      dataReadyRecorded.current = true;
      const loadDuration = performance.now() - mountTime.current;
      spanRef.current.setAttribute('report.data_load_time_ms', loadDuration);
    }
  }, [isDataReady]);

  // Start the first step only after we have the asset count.
  // This ensures the animation timing is calculated correctly from the start,
  // preventing the animation from completing too quickly for large datasets.
  useEffect(() => {
    if (!assetCountQuery.isLoading) {
      setCurrentStep(0);
    }
  }, [assetCountQuery.isLoading]);

  // Handle step progression
  useEffect(() => {
    if (currentStep < 0 || currentStep >= stepCount) {
      return;
    }

    // On the last step, wait for data to be ready before completing.
    // This prevents the animation from finishing before actual data loading
    // is complete, which would show all checkmarks while still loading.
    const isLastStep = currentStep === stepCount - 1;
    if (isLastStep && !isDataReady) {
      return;
    }

    const duration = isDataReady
      ? ACCELERATED_STEP_DURATION_MS
      : stepDurations[currentStep];

    const timer = setTimeout(() => {
      setCompletedSteps((prev) => new Set(prev).add(currentStep));

      if (currentStep < stepCount - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentStep(stepCount);
      }
    }, duration);

    return () => {
      clearTimeout(timer);
    };
  }, [currentStep, isDataReady, stepDurations, stepCount]);

  // Fire onComplete callback when all steps are done
  const handleComplete = useCallback(() => {
    // End the Sentry span (span duration = perceived time)
    if (spanRef.current) {
      const perceivedDuration = performance.now() - mountTime.current;
      spanRef.current.setAttribute(
        'report.perceived_time_ms',
        perceivedDuration
      );
      spanRef.current.end();
      spanRef.current = null;
    }

    if (onComplete) {
      onComplete();
    }
  }, [onComplete]);

  useEffect(() => {
    if (currentStep >= stepCount) {
      const timer = setTimeout(handleComplete, COMPLETION_DELAY_MS);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [currentStep, stepCount, handleComplete]);

  return {
    currentStep,
    completedSteps,
    isComplete: currentStep >= stepCount,
  };
}
